import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Row, Text, Badge, Panel, List, ListItem, Button, Tag, EmptyState } from '../ui';
import { api } from '../api/client';

type SourceTab = 'internal'|'external'|'all';

function statusTone(s: string): 'neutral'|'positive'|'negative'|'warning' {
  if (s === 'accepted') return 'positive';
  if (s === 'rejected') return 'negative';
  if (s === 'pending_review') return 'warning';
  return 'neutral';
}

export function Opportunities() {
  const [tab, setTab] = useState<SourceTab>('internal');
  const [allOpps, setAllOpps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState<string|null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    setLoading(true);
    api.opportunities('all').then(r => setAllOpps(r.opportunities)).catch(() => setAllOpps([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const internal = allOpps.filter(o => o.source === 'internal');
  const external = allOpps.filter(o => o.source === 'external');
  const visible = tab === 'internal' ? internal : tab === 'external' ? external : allOpps;
  const sorted = [...visible].sort((a, b) => {
    if (a.source === 'external' && a.externalStatus === 'pending_review') return -1;
    if (b.source === 'external' && b.externalStatus === 'pending_review') return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  async function decide(id: string, decision: 'accepted'|'rejected') {
    setDeciding(id);
    try { await api.externalDecision(id, decision); refresh(); } finally { setDeciding(null); }
  }

  const tabs: { id: SourceTab; label: string; count: number }[] = [
    { id: 'internal', label: 'Posted by us', count: internal.length },
    { id: 'external', label: 'External submissions', count: external.length },
    { id: 'all', label: 'All', count: allOpps.length },
  ];

  return (
    <Stack gap={3}>
      <Row justify="between" align="center">
        <Stack gap={0.5}>
          <Text variant="display">Opportunities</Text>
          <Text muted variant="small">Deal opportunities posted by the group or submitted externally.</Text>
        </Stack>
      </Row>
      <div className="flex overflow-x-auto whitespace-nowrap hairline-b no-scrollbar">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-ink text-ink' : 'border-transparent text-muted hover:text-ink'}`}>
            {t.label}
            {t.count > 0 && <span className={`ml-1 sm:ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-ink text-bg' : 'bg-whisper text-muted'}`}>{t.count}</span>}
          </button>
        ))}
      </div>
      <Panel>
        {loading ? <Text variant="label">loading...</Text>
          : sorted.length === 0 ? <EmptyState title={tab === 'external' ? 'no external submissions yet' : 'no opportunities yet'} hint={tab === 'external' ? 'submissions appear here when someone uses the public intake link' : 'opportunities posted by your group appear here'} />
          : <List>
              {sorted.map((o: any) => (
                <ListItem key={o.id}>
                  <div className="py-1">
                    <Stack gap={2}>
                      <div className="flex items-start gap-2">
                        <Stack gap={0.5} className="flex-1 min-w-0">
                          <Row gap={1} align="center" wrap>
                            <button onClick={() => navigate(`/opportunities/${o.id}`)} className="text-sm font-medium hover:underline text-left">{o.title}</button>
                            {o.source === 'external' && <Badge tone={statusTone(o.externalStatus)}>{o.externalStatus === 'pending_review' ? 'pending review' : o.externalStatus}</Badge>}
                            {o.status !== 'open' && <Tag>{o.status}</Tag>}
                          </Row>
                          {o.source === 'external' && (
                            <Text variant="label" className="text-muted">from {o.externalSubmitterName}{o.externalSubmitterCompany ? ` · ${o.externalSubmitterCompany}` : ''}{o.externalSubmitterEmail ? ` · ${o.externalSubmitterEmail}` : ''}</Text>
                          )}
                          <Row gap={2} wrap>
                            {o.sector && <Text variant="label">{o.sector}</Text>}
                            {o.geography && <Text variant="label">{o.geography}</Text>}
                            {o.ticketSizeRange && <Text variant="label">{o.ticketSizeRange}</Text>}
                          </Row>
                        </Stack>
                      </div>
                      {o.source === 'external' && o.externalStatus === 'pending_review' && (
                        <div className="flex gap-2">
                          <Button variant="primary" size="sm" className="flex-1 sm:flex-none justify-center" disabled={deciding === o.id} onClick={() => decide(o.id, 'accepted')}>Accept</Button>
                          <Button variant="secondary" size="sm" className="flex-1 sm:flex-none justify-center" disabled={deciding === o.id} onClick={() => decide(o.id, 'rejected')}>Reject</Button>
                        </div>
                      )}
                    </Stack>
                  </div>
                </ListItem>
              ))}
            </List>}
      </Panel>
    </Stack>
  );
}
